// Verdichtete Daten der Auswertungen: Stand ansehen, einen Zeitraum neu aufbauen.
import { defineContract } from '@shared/contract'
import { z } from 'zod'
import { REBUILD, zDay } from './common'

export const REBUILD_MAX_DAYS = 400

export const dataStatus = defineContract({
  name: 'reporting.data.status',
  title: 'Stand der Auswertungsdaten anzeigen',
  description: 'Bis zu welchem Tag die Tageswerte verdichtet sind, wann der letzte Lauf war und welche Tage seither nachgebucht wurden.',
  llm: {
    description:
      'Nennt den Stand der verdichteten Auswertungsdaten: letzter Lauf, erster und letzter verdichteter Tag, Zahl der Tage mit jüngeren Buchungen. ' +
      'Verwenden, wenn jemand fragt, wie aktuell die Auswertungen sind. Die Auswertungen selbst rechnen nachgebuchte Tage ohnehin aus den Belegen.',
    keywords: ['Stand der Auswertungen', 'wie aktuell', 'Nachtlauf'],
  },
  input: z.strictObject({}),
  output: z.object({
    builtAt: z.date().nullable(),
    firstDay: zDay.nullable(),
    lastDay: zDay.nullable(),
    dayCount: z.int(),
    /** verdichtete Tage, zu denen es jüngere Belege oder Bewegungen gibt – sie werden beim Lesen frisch gerechnet */
    staleDays: z.array(zDay),
    label: z.string(),
    /** nächster planmäßiger Lauf als Satz */
    schedule: z.string(),
  }),
  permission: REBUILD,
  risk: 'read',
})

export const dataRebuild = defineContract({
  name: 'reporting.data.rebuild',
  title: 'Auswertungsdaten neu aufbauen',
  description: 'Baut die verdichteten Tageswerte (Umsatz je Tag, Bestand je Tag) für einen Zeitraum aus Belegen und Bewegungsjournal neu auf.',
  llm: {
    description:
      'Baut die verdichteten Tageswerte der Auswertungen für die Tage von `fromDate` bis `toDate` neu auf – wiederholbar, ohne eine Buchung zu ändern. ' +
      'Nur verwenden, wenn die Verwaltung das ausdrücklich verlangt (nach einer Datenübernahme). Nicht nötig, um aktuelle Zahlen zu sehen: Die Auswertungen rechnen heute und nachgebuchte Tage von selbst aus den Belegen.',
    keywords: ['Auswertungen neu aufbauen', 'Tageswerte neu rechnen'],
  },
  input: z
    .strictObject({ fromDate: zDay, toDate: zDay })
    .refine((input) => input.fromDate <= input.toDate, { error: 'Der Zeitraum endet vor seinem Beginn.', path: ['toDate'] }),
  output: z.object({
    dayCount: z.int(),
    salesRows: z.int(),
    stockRows: z.int(),
    /** Zahl der Tage, deren Werte sich gegenüber dem vorigen Stand geändert haben (0 = alles war aktuell) */
    changedDays: z.int(),
    /** Tage, die während des Laufs noch gebucht wurden und deshalb weiter aus den Belegen gerechnet werden */
    pendingDays: z.int(),
    message: z.string(),
  }),
  permission: REBUILD,
  risk: 'write',
  // Sammelaktion über viele Tage: dem Assistenten nur, wenn der Betrieb „Erweiterte Aktionen“ einschaltet
  assistant: 'opt-in',
  limits: { maxItems: REBUILD_MAX_DAYS, itemCount: (input) => Math.round((Date.parse(input.toDate) - Date.parse(input.fromDate)) / 86_400_000) + 1 },
})
